import { Link } from "react-router-dom";
import { ArrowRight, HandHeart, LibraryBig, GraduationCap, Users, LifeBuoy, Briefcase } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn, Eyebrow, Tag } from "@/components/Motion";
import Marquee from "@/components/Marquee";
import { KITTY } from "@/lib/brand";

const VISION = [
    { icon: HandHeart, title: "Outreach Programs", copy: "Showing up for the neighborhoods and networks that raised us — consistently, not just for cameras." },
    { icon: LibraryBig, title: "Shared Resources", copy: "Knowledge, tools, and access passed forward. Nothing gatekept that could set someone free." },
    { icon: GraduationCap, title: "Youth Creative Support", copy: "Mentorship, direction, and real opportunities for young creatives before the world talks them out of it." },
    { icon: Users, title: "Collaboration Wall", copy: "Creators finding each other across lanes — designers meeting directors, artists meeting engineers." },
    { icon: LifeBuoy, title: "Emergency Assistance Fund", copy: "A future relief fund for community members in crisis — part of the PURP ALERT INC. nonprofit vision.", future: true },
    { icon: Briefcase, title: "Opportunities & Gigs", copy: "Connecting willing workers to real projects, paid collabs, and first clients." },
];

export default function Community() {
    return (
        <div data-testid="community-page">
            <PageHero
                eyebrow="Community"
                titleLines={["From every walk of life.", "For anyone willing", "to do the work."]}
                copy="The Network is community support with urgency and purpose — outreach, resources, collaboration, and a future of real assistance for people building something of their own."
            />

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="community-vision-grid">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">The Vision</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            What community <span className="italic text-purp">actually does.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {VISION.map((v, i) => (
                            <FadeIn key={v.title} delay={0.07 * i}>
                                <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-7 hover:border-purp/50 transition-colors duration-300" data-testid={`vision-card-${i}`}>
                                    <div className="flex items-center justify-between">
                                        <v.icon className="w-6 h-6 text-purp-neon" />
                                        {v.future && <Tag tone="volt">Future</Tag>}
                                    </div>
                                    <h3 className="mt-8 font-display font-bold uppercase text-2xl leading-tight">{v.title}</h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{v.copy}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <Marquee items={["Outreach", "Resources", "Youth Support", "Collaboration", "Opportunity", "All Walks of Life"]} />

            <section className="py-20 md:py-28" data-testid="community-nonprofit-note">
                <div className="max-w-4xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <div className="rounded-3xl bg-panel/75 backdrop-blur-md border border-purp/35 card-glow p-8 md:p-12">
                            <Tag tone="volt">The Road Ahead</Tag>
                            <h2 className="mt-5 font-display font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
                                Today the Network. <span className="italic text-purp">Tomorrow the nonprofit.</span>
                            </h2>
                            <div className="mt-6 space-y-4 text-slate-400 text-sm md:text-base leading-relaxed">
                                <p>
                                    <span className="text-slate-100 font-semibold">PURP ALERT NETWORK</span> is the
                                    business (LLC) being built right now — the studio, the hub, the media brand, the
                                    merch engine funding the mission.
                                </p>
                                <p>
                                    <span className="text-slate-100 font-semibold">PURP ALERT INC.</span> is the
                                    separate, future nonprofit organization: emergency assistance, youth support, and
                                    outreach at scale. It is not yet formed and is not a 501(c)(3) — and we will keep
                                    saying that plainly until the day it changes.
                                </p>
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </section>

            <section className="py-24 md:py-32 border-t border-white/10 relative overflow-hidden" data-testid="community-cta">
                <div className="absolute inset-0 bg-radial-purp" />
                <div className="relative max-w-3xl mx-auto px-5 md:px-8 text-center">
                    <FadeIn>
                        <img
                            src={KITTY.inYoCity}
                            alt="PURP KITTY — In Yo City"
                            loading="lazy"
                            className="mx-auto w-52 md:w-64 rounded-3xl border border-purp/30 card-glow mb-8"
                            data-testid="community-cta-image"
                        />
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Community isn&apos;t watched. <span className="italic text-purp text-glow">It&apos;s joined.</span>
                        </h2>
                        <Link to="/get-involved" data-testid="community-get-involved-cta" className="mt-9 inline-flex items-center gap-2 rounded-full bg-purp px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)]">
                            Get Involved <ArrowRight className="w-4 h-4" />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
